import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import Avatar from "../common/Avatar";
import BottomSheet from "../common/BottomSheet";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import messageRequestService, {
  MESSAGE_REQUEST_INTENTS,
  MESSAGE_REQUEST_INTENT_BY_KEY,
} from "../../services/messageRequestApi";

const MANUAL_MAX = 60;

// "Request pending" chip (design 1l) — quiet + terminal: it never turns into
// "declined". Mount it on agent/club profiles and search rows in place of the
// "Message" action once the viewer has an outstanding request to that person.
export const RequestPendingBadge = ({ compact = false, t = (s) => s }) => (
  <span
    className="inline-flex items-center gap-1.5 font-semibold text-[#667085]"
    style={{
      padding: compact ? "6px 12px" : "8px 14px",
      background: "#F2F4F7",
      // Search-row chip 8px (1l), profile chip 9px.
      borderRadius: compact ? 8 : 9,
      fontSize: compact ? "11.5px" : "12.5px",
    }}
  >
    <svg width={compact ? 10 : 11} height={compact ? 10 : 11} viewBox="0 0 12 12" fill="none">
      <circle cx="6" cy="6" r="4.6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6 3.8V6L7.6 7.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
    {t("Request pending")}
  </span>
);

const firstNameOf = (name) => (name || "").trim().split(/\s+/)[0] || name || "";

// MessageRequestComposeModal — the cold-contact gate, sender side (design frames
// 1i / 1j / 1k, capped state 1r; mobile bottom sheet 1u). Intent (5 curated
// intents; Manual reveals a short free-text field, 60-char counter) + a first
// message that travels with the request. The sender-side vocabulary has exactly
// two words: pending, accepted (1k) — no read state, no "declined".
//
// Presentation switches on viewport: desktop = centered portal dialog (1i/1j/1k),
// mobile (≤767px) = BottomSheet with the intent as an always-open radio list (1u).
//
// Props:
//   recipient   { agent_id, name, agency?, subtitle?, profile_picture_url? }
//               agent_id = the Agent row PK (the backend keys the request by
//               `recipient_agent_id`, confirmed P2-BE-1 — NOT account/profile id).
//               subtitle = the "Agent · Bosphorus XI · Istanbul" line (design 1i).
//   onClose     () => void
//   onSent      (recipient) => void — lets the caller flip the card/profile to
//               <RequestPendingBadge/> (design 1l). Cross-org (201) path only.
//   onSameOrg   ({channelId, channelType}) => void — the same-org 200 outcome:
//               the recipient is a colleague, so the backend opened a free chat
//               and filed NO request row. The caller opens that channel (via the
//               /inbox directChat nav contract) instead of the pending state.
//   cooldownStatus { blocked, blocked_until, open_count, cap } — the 1r capped
//               state. Optional; when omitted the modal fetches it on open.
//   onViewPending () => void — escape-hatch link "View your N pending requests".
//   t           i18n passthrough (identity default = verbatim copy).
const MessageRequestComposeModal = ({
  recipient,
  onClose,
  onSent,
  onSameOrg,
  cooldownStatus: cooldownProp,
  onViewPending,
  t = (s) => s,
}) => {
  const [intent, setIntent] = useState("");
  const [manualText, setManualText] = useState("");
  const [message, setMessage] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(cooldownProp || null);

  // Matches Tailwind's `md` breakpoint (BottomSheet is `md:hidden`), so the
  // sheet and the portal never both show.
  const isMobile = useMediaQuery("(max-width: 767px)");

  // ESC + body-scroll lock (portal modal pattern, mirrors ChatActionModal). The
  // BottomSheet manages its own scroll lock, so only wire this for the portal.
  useEffect(() => {
    if (isMobile) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "unset";
    };
  }, [onClose, isMobile]);

  // Fetch the cooldown/cap state on open unless the caller passed it in.
  useEffect(() => {
    let cancelled = false;
    if (!cooldownProp) {
      messageRequestService.cooldownStatus().then((res) => {
        if (!cancelled && res.success) setCooldown(res.data);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [cooldownProp]);

  const blocked = !!cooldown?.blocked;
  const cap = cooldown?.cap;
  const openCount = cooldown?.open_count ?? cap;

  if (!recipient) return null;

  const first = firstNameOf(recipient.name);
  const selectedIntent = MESSAGE_REQUEST_INTENT_BY_KEY[intent];
  const manualReady = intent !== "manual" || manualText.trim().length > 0;
  const canSend =
    !!intent && manualReady && message.trim().length > 0 && !submitting && !blocked;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSend) return;
    setSubmitting(true);
    setError(null);
    const res = await messageRequestService.createRequest({
      recipient_agent_id: recipient.agent_id,
      intent,
      intent_manual_text: intent === "manual" ? manualText.trim() : undefined,
      message: message.trim(),
    });
    setSubmitting(false);
    if (res.success) {
      if (res.same_org) {
        // Same-org colleague (200): the backend opened a free chat and filed no
        // request row — skip the "Request sent"/pending state and open the chat.
        if (onSameOrg) {
          onSameOrg({
            channelId: res.channel_cid,
            channelType: res.channel_type,
          });
        } else {
          onClose?.();
        }
        return;
      }
      setSent(true);
      onSent?.(recipient);
    } else if (res.cooldown) {
      // Cap hit mid-send (429): flip to the 1r capped banner instead of an error.
      setCooldown(res.cooldown);
    } else {
      setError(res.error || t("Failed to send request"));
    }
  };

  // ── Manual free-text (1j) — shared by desktop dropdown + mobile radio list ──
  const manualField = intent === "manual" && (
    <div
      className="flex items-center justify-between rounded-[10px]"
      style={{ padding: "9px 12px", border: "1px solid #E4E9F0", background: "#FDFDFE" }}
    >
      <input
        value={manualText}
        maxLength={MANUAL_MAX}
        onChange={(e) => setManualText(e.target.value)}
        placeholder={t("Write your own intent in a few words")}
        className="flex-1 bg-transparent text-[13px] text-[#101828] placeholder:text-[#98A2B3] focus:outline-none"
      />
      <span className="ml-2 flex-shrink-0 text-[10.5px] text-[#98A2B3]">
        {manualText.length}/{MANUAL_MAX}
      </span>
    </div>
  );

  // Recipient identity chip (1i/1j desktop, 1u mobile).
  const recipientChip = (
    <div
      className="flex items-center gap-2.5 rounded-[10px]"
      style={{ padding: "9px 12px", background: "#F8FAFC", border: "1px solid #EDF0F4" }}
    >
      <Avatar
        src={recipient.profile_picture_url}
        alt={recipient.name}
        fallbackText={recipient.name}
        size={34}
      />
      <div className="flex min-w-0 flex-col" style={{ gap: 1 }}>
        <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#101828]">
          {recipient.name}
          <span
            className="rounded-[6px] font-semibold text-[#1466D8]"
            style={{ padding: "1px 6px", background: "#F0F6FF", border: "1px solid #D6E6FF", fontSize: "9.5px" }}
          >
            {t("Agent")}
          </span>
        </span>
        {recipient.subtitle && (
          <span className="truncate text-[11px] text-[#667085]">{recipient.subtitle}</span>
        )}
      </div>
    </div>
  );

  // Blue info-tone cap banner (design 1r): "friendly, never punitive" — a quiet
  // clock glyph, NOT an amber warning triangle.
  const cappedBanner = blocked && (
    <div
      className="flex gap-3 rounded-[12px]"
      style={{ padding: "14px 16px", background: "#F0F6FF", border: "1px solid #D6E6FF" }}
    >
      <div
        className="flex flex-shrink-0 items-center justify-center rounded-full"
        style={{ width: 34, height: 34, background: "#EAF2FF" }}
      >
        <svg width="15" height="15" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="5.4" stroke="#1466D8" strokeWidth="1.5" />
          <path d="M7 4.4V7L8.8 8.2" stroke="#1466D8" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
      <div className="flex flex-col gap-[3px]">
        {/* Frame 1r copy, verbatim. The cap is NOT a 24h pause — a slot is freed
            only by a terminal state on a pending request (accept / withdraw /
            expire, D12), so the wording is about hygiene ("that's the most that
            can wait"), never a countdown and never spam vocabulary. */}
        <span className="text-[13.5px] font-bold text-[#1466D8]">
          {t("You have")} {openCount} {t("open requests")}
        </span>
        <span className="text-[12px] leading-[1.5] text-[#475467]">
          {t(
            "That's the most that can wait at once. New sends unlock as agents accept — or as you withdraw a pending request.",
          )}
        </span>
      </div>
    </div>
  );

  // First-message field (1i/1j/1u). Design 1i: it stays dimmed + disabled until
  // an intent is picked, so the intent is always the first choice.
  const messageGated = !intent;
  const firstMessageField = (
    <div
      className="flex flex-col gap-1.5"
      style={{ opacity: messageGated ? 0.55 : 1 }}
    >
      <span className="text-[12px] font-semibold text-[#475467]">{t("First message")}</span>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        disabled={messageGated}
        placeholder={
          messageGated
            ? t("Choose an intent first…")
            : t(
                "Introduce yourself and be concrete. This message travels with your request.",
              )
        }
        className="min-h-[96px] resize-y rounded-[10px] text-[13px] leading-[1.5] text-[#101828] placeholder:text-[#98A2B3] focus:outline-none focus:border-[#2283FF] disabled:cursor-not-allowed disabled:bg-[#F8FAFC]"
        style={{ padding: "10px 12px", border: "1px solid #E4E9F0" }}
      />
    </div>
  );

  // Intent field — desktop is a dropdown trigger + popover (1i/1j); mobile is an
  // always-open radio list (1u). When blocked (1r) the whole block dims.
  const desktopIntentField = (
    <div
      className="flex flex-col gap-1.5"
      style={{ opacity: blocked ? 0.45 : 1, pointerEvents: blocked ? "none" : "auto" }}
    >
      <span className="text-[12px] font-semibold text-[#475467]">{t("Intent")}</span>
      <button
        type="button"
        onClick={() => setDropdownOpen((v) => !v)}
        className="flex items-center justify-between rounded-[10px]"
        style={{
          padding: "9px 12px",
          border: dropdownOpen ? "1.5px solid #2283FF" : "1px solid #E4E9F0",
          boxShadow: dropdownOpen ? "0 0 0 3px rgba(34,131,255,.12)" : "none",
        }}
      >
        {selectedIntent ? (
          <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#101828]">
            <span
              className="inline-block rounded-full"
              style={{ width: 8, height: 8, background: selectedIntent.dot }}
            />
            {selectedIntent.label}
          </span>
        ) : (
          <span className="text-[13px] text-[#98A2B3]">
            {t("Choose why you're reaching out…")}
          </span>
        )}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d={dropdownOpen ? "M2.5 7.5L6 4L9.5 7.5" : "M2.5 4.5L6 8L9.5 4.5"}
            stroke="#667085"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {dropdownOpen && (
        <div
          className="overflow-hidden rounded-[12px]"
          style={{ border: "1px solid #E4E9F0", boxShadow: "0 12px 32px rgba(16,24,40,.12)" }}
        >
          {MESSAGE_REQUEST_INTENTS.map((it, i) => {
            const active = it.key === intent;
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  setIntent(it.key);
                  setDropdownOpen(false);
                }}
                className="flex w-full items-center gap-2.5 text-left"
                style={{
                  padding: "10px 13px",
                  background: active ? "#F0F6FF" : "#FFFFFF",
                  borderTop: i === 0 ? "none" : "1px solid #F0F2F6",
                }}
              >
                <span
                  className="inline-block flex-shrink-0 rounded-full"
                  style={{ width: 8, height: 8, background: it.dot }}
                />
                <span className="flex flex-col">
                  <span
                    className="text-[13px] font-semibold"
                    style={{ color: active ? "#1466D8" : "#101828" }}
                  >
                    {it.label}
                  </span>
                  <span className="text-[11px]" style={{ color: active ? "#667085" : "#98A2B3" }}>
                    {t(it.desc)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {manualField}
    </div>
  );

  // Mobile intent radio list (design 1u): all options always visible, a radio
  // dot per row, selected row tinted blue.
  const mobileIntentField = (
    <div
      className="flex flex-col gap-1.5"
      style={{ opacity: blocked ? 0.45 : 1, pointerEvents: blocked ? "none" : "auto" }}
    >
      <span className="text-[12.5px] font-semibold text-[#475467]">{t("Intent")}</span>
      <div className="overflow-hidden rounded-[12px]" style={{ border: "1px solid #E4E9F0" }}>
        {MESSAGE_REQUEST_INTENTS.map((it, i) => {
          const active = it.key === intent;
          return (
            <button
              key={it.key}
              type="button"
              onClick={() => setIntent(it.key)}
              className="flex w-full items-center gap-2.5 text-left"
              style={{
                padding: "12px 13px",
                background: active ? "#F0F6FF" : "#FFFFFF",
                borderTop: i === 0 ? "none" : "1px solid #F0F2F6",
              }}
            >
              <span
                className="flex-shrink-0 rounded-full"
                style={{
                  width: 18,
                  height: 18,
                  boxSizing: "border-box",
                  border: active ? "5.5px solid #2283FF" : "1.5px solid #D3DAE3",
                }}
              />
              <span
                className="text-[13.5px]"
                style={{
                  fontWeight: active ? 600 : 500,
                  color: active ? "#1466D8" : "#101828",
                }}
              >
                {it.key === "manual" ? (
                  <>
                    {t("Manual")}{" "}
                    <span className="font-normal text-[#98A2B3]">{t("— write your own")}</span>
                  </>
                ) : (
                  it.label
                )}
              </span>
            </button>
          );
        })}
      </div>
      {manualField}
    </div>
  );

  // ── Sent state (1k) ──────────────────────────────────────────────────────
  const SentBody = (
    <div className="flex flex-col items-center gap-3 text-center" style={{ padding: "8px 0" }}>
      <div
        className="flex items-center justify-center rounded-full"
        style={{ width: 56, height: 56, background: "#EAF6EE" }}
      >
        <Check size={24} className="text-[#1B9E52]" />
      </div>
      <span className="text-[16px] font-bold text-[#101828]">{t("Request sent")}</span>
      <span className="text-[12.5px] leading-[1.55] text-[#667085]">
        {t("You can chat with")} {recipient.name} {t("once")} {first} {t("accepts.")}{" "}
        {t("We'll let you know. Nothing else to do here.")}
      </span>
      <button
        type="button"
        onClick={onClose}
        className="rounded-[9px] text-[13px] font-semibold text-[#475467]"
        style={{ padding: "9px 26px", background: "#FFFFFF", border: "1px solid #E4E9F0" }}
      >
        {t("Done")}
      </button>
    </div>
  );

  // ── Desktop compose / capped state (1i / 1j / 1r) ─────────────────────────
  const ComposeBody = (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
      <div className="flex items-center gap-2">
        <span className="text-[16px] font-bold text-[#101828]">
          {t("Message request")}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto text-[#98A2B3] hover:text-[#101828]"
          aria-label={t("Cancel")}
        >
          <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {recipientChip}
      {cappedBanner}
      {desktopIntentField}

      <div style={{ opacity: blocked ? 0.45 : 1, pointerEvents: blocked ? "none" : "auto" }}>
        {firstMessageField}
      </div>

      {error && <p className="text-[12.5px] text-[#B42318]">{error}</p>}

      {/* Footer note + send / escape hatch */}
      {blocked ? (
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onViewPending}
            className="text-[12px] font-semibold text-[#1466D8]"
          >
            {t("View your")} {openCount} {t("pending requests")}
          </button>
          <span
            className="flex-shrink-0 rounded-[9px] text-[13px] font-semibold text-[#98A2B3]"
            style={{ padding: "9px 18px", background: "#E9EDF2" }}
          >
            {t("Send request")}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2.5">
          <span className="flex-1 text-[11px] leading-[1.45] text-[#98A2B3]">
            {first} {t("sees your intent and first message. You can chat once")} {first}{" "}
            {t("accepts.")}
          </span>
          <button
            type="submit"
            disabled={!canSend}
            className="flex-shrink-0 rounded-[9px] text-[13px] font-semibold text-white transition-colors"
            style={{
              padding: "9px 18px",
              background: canSend ? "#2283FF" : "#A8CDFB",
              boxShadow: canSend ? "0 1px 2px rgba(34,131,255,.35)" : "none",
            }}
          >
            {submitting ? t("Sending…") : t("Send request")}
          </button>
        </div>
      )}
    </form>
  );

  // ── Mobile bottom sheet (1u) ──────────────────────────────────────────────
  if (isMobile) {
    const sheetFooter = sent ? null : blocked ? (
      <div className="flex flex-col items-center gap-2">
        <span
          className="w-full rounded-[12px] text-center text-[15px] font-semibold text-[#98A2B3]"
          style={{ padding: "13px 0", background: "#E9EDF2" }}
        >
          {t("Send request")}
        </span>
        <button
          type="button"
          onClick={onViewPending}
          className="text-[12px] font-semibold text-[#1466D8]"
        >
          {t("View your")} {openCount} {t("pending requests")}
        </button>
      </div>
    ) : (
      <div className="flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSend}
          className="w-full rounded-[12px] text-center text-[15px] font-semibold text-white"
          style={{
            padding: "13px 0",
            background: canSend ? "#2283FF" : "#A8CDFB",
            boxShadow: canSend ? "0 1px 2px rgba(34,131,255,.35)" : "none",
          }}
        >
          {submitting ? t("Sending…") : t("Send request")}
        </button>
        <span className="text-center text-[11.5px] leading-[1.45] text-[#98A2B3]">
          {t("You can chat once")} {first} {t("accepts.")}
        </span>
      </div>
    );

    return (
      <BottomSheet
        open
        onClose={onClose}
        title={sent ? t("Request sent") : t("Message request")}
        footer={sheetFooter}
      >
        {sent ? (
          SentBody
        ) : (
          <div className="flex flex-col gap-3.5 pb-1">
            {recipientChip}
            {cappedBanner}
            {mobileIntentField}
            <div style={{ opacity: blocked ? 0.45 : 1, pointerEvents: blocked ? "none" : "auto" }}>
              {firstMessageField}
            </div>
            {error && <p className="text-[12.5px] text-[#B42318]">{error}</p>}
          </div>
        )}
      </BottomSheet>
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full bg-white"
        style={{
          maxWidth: 480,
          borderRadius: 16,
          boxShadow: "0 24px 64px rgba(9,14,26,.45)",
          padding: "22px 24px",
        }}
      >
        {sent ? SentBody : ComposeBody}
      </div>
    </div>,
    document.body,
  );
};

export default MessageRequestComposeModal;
